<template>
  <div class="flex flex-col gap-3 text-left" :data-testid="`${testidPrefix}-attachments-${leg}`">
    <!-- Lista de anexos: o preview é um por vez, então a lista é a navegação.
         Some com um anexo só — um "tablist" de um item é ruído. -->
    <ul v-if="attachments.length > 1" class="m-0 flex flex-wrap gap-2 p-0" role="tablist">
      <li v-for="(attachment, index) in attachments" :key="attachment.evidenceId" class="list-none">
        <button
          type="button"
          role="tab"
          class="flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors"
          :class="
            index === selectedIndex
              ? 'border-primary30 bg-primary30 font-semibold text-white'
              : 'border-gray-300 text-textPrimary hover:bg-gray-100'
          "
          :aria-selected="index === selectedIndex"
          :data-testid="`${testidPrefix}-attachment-${attachment.evidenceId}-button`"
          @click="selectAttachment(index)"
        >
          <svg class="h-4 w-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
            <path :d="mdiFileOutline" fill="currentColor" />
          </svg>
          <span class="max-w-[160px] truncate">{{ attachment.originalFileName }}</span>
        </button>
      </li>
    </ul>

    <div
      v-if="isLoading"
      class="min-h-[300px] w-full animate-pulse rounded-lg bg-lightGray30"
      :data-testid="`${testidPrefix}-attachments-${leg}-loading`"
    />

    <div
      v-else-if="loadError"
      class="flex min-h-[240px] w-full flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 p-6 text-center"
      :data-testid="`${testidPrefix}-attachments-${leg}-error`"
      role="alert"
    >
      <svg class="h-10 w-10 text-negative" viewBox="0 0 24 24" aria-hidden="true">
        <path :d="mdiAlertCircleOutline" fill="currentColor" />
      </svg>
      <p class="m-0 text-sm text-textPrimary">{{ loadError }}</p>
      <OutlineButton
        text="Tentar novamente"
        width="200px"
        :data-testid="`${testidPrefix}-attachments-${leg}-retry-button`"
        @click="load"
      />
    </div>

    <template v-else-if="currentUrl">
      <StepPreview
        :file="null"
        :file-url="currentUrl"
        :file-type="currentType"
        :rotation="rotation"
        :zoom="zoom"
        mode="preview"
        :show-display-name="false"
        @update:rotation="rotation = $event"
        @update:zoom="zoom = $event"
      />
      <p class="m-0 truncate text-xs text-neutral50">
        {{ currentAttachment?.originalFileName }}
      </p>
    </template>

    <!-- Registro aprovado sem anexo é possível (equivalência por descrição). O
         painel continua valendo pelo CTA: a trilha e o parecer estão na fila. -->
    <div
      v-else
      class="flex min-h-[160px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center"
      :data-testid="`${testidPrefix}-attachments-${leg}-empty`"
    >
      <p class="m-0 text-sm text-neutral50">O Registro de Atividade aprovado não tem anexos.</p>
    </div>

    <div v-if="containerId" class="flex">
      <OutlineButton
        text="Abrir na fila de auditoria ↗"
        width="auto"
        height="32px"
        :data-testid="`${testidPrefix}-open-queue-${leg}-button`"
        @click="openQueue"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { mdiAlertCircleOutline, mdiFileOutline } from '@mdi/js'

import Network from '@/Network'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import StepPreview from '@/components/designSystem/modals/AttendanceUploadModal/StepPreview.vue'
import { DSAttendanceStatus } from '@/constants/ds-attendance.constants'
import { pathForStatus } from '@/modules/company/Approvals/DsAttendance/utils/status-route-map'
import type { DSAttendanceActivityAttachment } from '@/types/ds-attendance.types'
import type { StationLegKey } from '@/types/station-evidence.types'
import { getApiErrorMessage } from '@/utils/apiError'

const props = withDefaults(
  defineProps<{
    /** Container do Registro de Atividade que venceu o ranking desta perna. */
    containerId: number | null
    attachments?: DSAttendanceActivityAttachment[]
    leg: StationLegKey
    testidPrefix?: string
  }>(),
  { attachments: () => [], testidPrefix: 'station-evidence' }
)

const selectedIndex = ref(0)
const isLoading = ref(false)
const loadError = ref<string | null>(null)
const rotation = ref(0)
const zoom = ref(1)

/**
 * Cache por `evidenceId`: voltar a um anexo já aberto não baixa de novo, e a
 * revogação no unmount tem uma lista única para percorrer. O `Map` é SUBSTITUÍDO
 * a cada gravação — mutar in-place não dispara render no Vue 3.
 */
const objectUrls = ref(new Map<number, { url: string; type: string }>())

const currentAttachment = computed(() => props.attachments[selectedIndex.value] ?? null)
const currentEntry = computed(() =>
  currentAttachment.value ? objectUrls.value.get(currentAttachment.value.evidenceId) : undefined
)
const currentUrl = computed(() => currentEntry.value?.url ?? null)
const currentType = computed(() => currentEntry.value?.type)

function revokeAll() {
  objectUrls.value.forEach(({ url }) => URL.revokeObjectURL(url))
  objectUrls.value = new Map()
}

function selectAttachment(index: number) {
  selectedIndex.value = index
  rotation.value = 0
  zoom.value = 1
  void load()
}

async function load() {
  const attachment = currentAttachment.value
  const containerId = props.containerId
  if (!attachment || containerId == null) return
  if (objectUrls.value.has(attachment.evidenceId)) return

  // Mesmo guard dos outros painéis: dois cliques rápidos entre anexos deixariam a
  // resposta antiga apagar o skeleton do anexo atual — ou pior, plantar o erro
  // dela por cima de um anexo que carregou, já que o template prioriza o erro.
  const isCurrent = () =>
    props.containerId === containerId &&
    currentAttachment.value?.evidenceId === attachment.evidenceId

  isLoading.value = true
  loadError.value = null
  try {
    const blob = await Network.dsAttendance.getAttachmentFile(containerId, attachment.evidenceId)
    // A blob URL entra no cache mesmo fora de foco: o arquivo é do anexo certo, e
    // descartá-la aqui vazaria a memória dele.
    const next = new Map(objectUrls.value)
    next.set(attachment.evidenceId, { url: URL.createObjectURL(blob), type: blob.type })
    objectUrls.value = next
  } catch (error) {
    console.error('Erro ao baixar o anexo do Registro de Atividade:', error)
    if (isCurrent()) {
      loadError.value = getApiErrorMessage(error, 'Não foi possível abrir o anexo.')
    }
  } finally {
    if (isCurrent()) isLoading.value = false
  }
}

function openQueue() {
  if (!props.containerId) return
  // A rota sai do mapa canônico, não de uma segunda cópia da regra: só registro
  // APROVADO vira fonte vencedora. Nova aba para não perder o contexto da vaga.
  window.open(
    pathForStatus(props.containerId, DSAttendanceStatus.APPROVED),
    '_blank',
    'noopener,noreferrer'
  )
}

watch(
  () => [props.containerId, props.attachments] as const,
  () => {
    revokeAll()
    selectedIndex.value = 0
    void load()
  },
  { immediate: true }
)

onBeforeUnmount(revokeAll)
</script>
